import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../../services/api';
import {
  PROJECT_STATUSES, BOARD_COLUMNS, PRIORITIES, STATUS_LABEL, STATUS_COLOUR, PRIORITY_COLOUR, errMsg,
} from '../constants';
import TaskModal from './TaskModal';

const fmtDate = (s) => (s ? s.slice(0, 10) : '');
const initials = (name, email) => {
  const base = name || email || '?';
  return base.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
};
const ACTION_TEXT = {
  created: 'created', updated: 'updated', status_changed: 'changed status',
  assigned: 'assigned', completed: 'completed', commented: 'commented',
  resource_added: 'added a resource', resource_removed: 'removed a resource',
  archived: 'archived', task_deleted: 'deleted a task',
};

const ProjectDetail = ({ projectId, users, suggestions, onBack }) => {
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [taskView, setTaskView] = useState('board');
  const [openTaskId, setOpenTaskId] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [comment, setComment] = useState('');
  const [linkForm, setLinkForm] = useState({ kind: 'link', url: '', path: '', title: '' });
  const [uploading, setUploading] = useState(false);

  const userName = useCallback((id) => {
    const u = users.find((x) => x.id === id);
    return u ? (u.full_name || u.email) : (id ? `#${id}` : '—');
  }, [users]);

  // Reload in place: keep the existing `project` (and any open TaskModal) mounted
  // while refetching, so interactions don't unmount/flicker the modal.
  const load = useCallback(async () => {
    try {
      setProject(await projectsAPI.getProject(projectId));
    } catch (err) {
      setError(errMsg(err, 'Failed to load project'));
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const saveProject = async (patch) => {
    try {
      await projectsAPI.updateProject(projectId, patch);
      await load();
    } catch (err) {
      setError(errMsg(err, 'Failed to save'));
    }
  };

  const addTask = async () => {
    if (!newTaskTitle.trim()) return;
    await projectsAPI.createTask(projectId, { title: newTaskTitle.trim() });
    setNewTaskTitle('');
    await load();
  };

  const moveTask = async (taskId, status) => {
    await projectsAPI.updateTask(taskId, { title: tasksById[taskId].title, status });
    await load();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    await projectsAPI.addComment({ project_id: projectId, body: comment.trim() });
    setComment('');
    await load();
  };

  const addLink = async () => {
    const { kind, url, path, title } = linkForm;
    if (kind === 'link' && !url.trim()) return;
    if (kind === 'path' && !path.trim()) return;
    await projectsAPI.addResourceLink({ project_id: projectId, kind, url: url || null, path: path || null, title: title || null });
    setLinkForm({ kind: 'link', url: '', path: '', title: '' });
    await load();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await projectsAPI.uploadResource(file, { project_id: projectId, title: file.name });
      await load();
    } catch (err) {
      setError(errMsg(err, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadResource = async (id) => {
    try {
      const { url } = await projectsAPI.resourceDownloadUrl(id);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(errMsg(err, 'Could not get download link'));
    }
  };

  if (!project) return <div className="projects-app"><div className="proj-empty">Loading…</div></div>;

  const tasks = project.tasks || [];
  const tasksById = Object.fromEntries(tasks.map((t) => [t.id, t]));

  return (
    <div className="projects-app">
      <div className="projects-header">
        <div className="proj-detail-head">
          <button className="proj-btn proj-btn-link" onClick={onBack}><i className="bi bi-arrow-left" /> Projects</button>
          <h4 className="projects-title">{project.name}</h4>
          <span className="proj-badge" style={{ background: STATUS_COLOUR[project.status] }}>{STATUS_LABEL[project.status] || project.status}</span>
        </div>
        <button className="proj-btn" onClick={() => saveProject({ is_archived: !project.is_archived })}>
          <i className="bi bi-archive" /> {project.is_archived ? 'Unarchive' : 'Archive'}
        </button>
      </div>

      {error && <div className="proj-alert proj-alert-danger">{error}<button onClick={() => setError(null)}>×</button></div>}

      <div className="proj-detail-grid">
        {/* ── Metadata panel ── */}
        <div className="proj-meta-panel">
          <div className="proj-meta-row"><span>Status</span>
            <select className="proj-select" value={project.status} onChange={(e) => saveProject({ status: e.target.value })}>
              {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
          </div>
          <div className="proj-meta-row"><span>Priority</span>
            <select className="proj-select" value={project.priority} onChange={(e) => saveProject({ priority: e.target.value })}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="proj-meta-row"><span>Owner</span>
            <select className="proj-select" value={project.owner_id || ''} onChange={(e) => saveProject({ owner_id: e.target.value ? parseInt(e.target.value, 10) : null })}>
              <option value="">— Unassigned —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
            </select>
          </div>
          <div className="proj-meta-row"><span>Type</span>
            <input className="proj-input" list="pd-types" defaultValue={project.project_type || ''} onBlur={(e) => e.target.value !== (project.project_type || '') && saveProject({ project_type: e.target.value })} />
            <datalist id="pd-types">{suggestions.project_types.map((t) => <option key={t} value={t} />)}</datalist>
          </div>
          <div className="proj-meta-row"><span>Client</span>
            <input className="proj-input" list="pd-clients" defaultValue={project.client || ''} onBlur={(e) => e.target.value !== (project.client || '') && saveProject({ client: e.target.value })} />
            <datalist id="pd-clients">{suggestions.clients.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div className="proj-meta-row"><span>Platform</span>
            <input className="proj-input" list="pd-platforms" defaultValue={project.platform || ''} onBlur={(e) => e.target.value !== (project.platform || '') && saveProject({ platform: e.target.value })} />
            <datalist id="pd-platforms">{suggestions.platforms.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div className="proj-meta-row"><span>Due date</span>
            <input type="date" className="proj-input" defaultValue={fmtDate(project.due_date)} onChange={(e) => saveProject({ due_date: e.target.value || null })} />
          </div>
          <div className="proj-meta-row"><span>File location</span>
            <input className="proj-input" defaultValue={project.file_location || ''} placeholder="Dropbox / network path"
              onBlur={(e) => e.target.value !== (project.file_location || '') && saveProject({ file_location: e.target.value })} />
          </div>
          <div className="proj-meta-row proj-meta-desc"><span>Description</span>
            <textarea className="proj-input" rows={3} defaultValue={project.description || ''}
              onBlur={(e) => e.target.value !== (project.description || '') && saveProject({ description: e.target.value })} />
          </div>
        </div>

        {/* ── Tasks ── */}
        <div className="proj-tasks-area">
          <div className="proj-tasks-toolbar">
            <div className="proj-view-toggle">
              <button className={taskView === 'board' ? 'active' : ''} onClick={() => setTaskView('board')}>Board</button>
              <button className={taskView === 'list' ? 'active' : ''} onClick={() => setTaskView('list')}>List</button>
            </div>
            <div className="proj-add-task">
              <input className="proj-input" placeholder="Add a task and press Enter…" value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTask()} />
              <button className="proj-btn proj-btn-primary" onClick={addTask}>Add</button>
            </div>
          </div>

          {taskView === 'board' ? (
            <div className="proj-board">
              {BOARD_COLUMNS.map((col) => {
                const colTasks = tasks.filter((t) => t.status === col);
                return (
                  <div key={col} className="proj-board-col"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { const id = parseInt(e.dataTransfer.getData('text/plain'), 10); if (tasksById[id] && tasksById[id].status !== col) moveTask(id, col); }}>
                    <div className="proj-board-col-head" style={{ borderColor: STATUS_COLOUR[col] }}>
                      {STATUS_LABEL[col]} <span>{colTasks.length}</span>
                    </div>
                    {colTasks.map((t) => (
                      <div key={t.id} className="proj-card" draggable
                        onDragStart={(e) => e.dataTransfer.setData('text/plain', String(t.id))}
                        onClick={() => setOpenTaskId(t.id)}>
                        <div className="proj-card-title">{t.title}</div>
                        <div className="proj-card-meta">
                          {t.assignee_id && <span className="proj-avatar" title={userName(t.assignee_id)}>{initials(userName(t.assignee_id))}</span>}
                          {t.due_date && <span className="proj-card-due">{fmtDate(t.due_date)}</span>}
                          <span className="proj-prio-dot" style={{ background: PRIORITY_COLOUR[t.priority] }} title={t.priority} />
                        </div>
                      </div>
                    ))}
                    {colTasks.length === 0 && <div className="proj-board-empty">—</div>}
                  </div>
                );
              })}
            </div>
          ) : (
            <table className="projects-table proj-tasks-table">
              <thead><tr><th>Task</th><th>Status</th><th>Assignee</th><th>Due</th><th>Priority</th></tr></thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} onClick={() => setOpenTaskId(t.id)}>
                    <td>{t.title}</td>
                    <td><span className="proj-badge" style={{ background: STATUS_COLOUR[t.status] }}>{STATUS_LABEL[t.status] || t.status}</span></td>
                    <td>{userName(t.assignee_id)}</td>
                    <td>{fmtDate(t.due_date) || '—'}</td>
                    <td><span className="proj-prio" style={{ color: PRIORITY_COLOUR[t.priority] }}>{t.priority}</span></td>
                  </tr>
                ))}
                {tasks.length === 0 && <tr><td colSpan={5} className="proj-empty">No tasks yet.</td></tr>}
              </tbody>
            </table>
          )}

          {/* ── Resources ── */}
          <div className="proj-section">
            <h6>Resources</h6>
            <div className="proj-resource-add">
              <select className="proj-select" value={linkForm.kind} onChange={(e) => setLinkForm({ ...linkForm, kind: e.target.value })} style={{ width: 90 }}>
                <option value="link">Link</option><option value="path">Path</option>
              </select>
              {linkForm.kind === 'link'
                ? <input className="proj-input" placeholder="https://…" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} />
                : <input className="proj-input" placeholder="Dropbox / local path" value={linkForm.path} onChange={(e) => setLinkForm({ ...linkForm, path: e.target.value })} />}
              <input className="proj-input" placeholder="Label (optional)" value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })} style={{ maxWidth: 160 }} />
              <button className="proj-btn" onClick={addLink}>Add</button>
              <label className="proj-btn proj-btn-upload">{uploading ? 'Uploading…' : 'Upload file'}
                <input type="file" hidden onChange={uploadFile} disabled={uploading} />
              </label>
            </div>
            <ul className="proj-resource-list">
              {(project.resources || []).map((r) => (
                <li key={r.id}>
                  <i className={`bi ${r.kind === 'file' ? 'bi-paperclip' : r.kind === 'path' ? 'bi-folder' : 'bi-link-45deg'}`} />
                  {r.kind === 'path'
                    ? <span className="proj-resource-path" title={r.path}>{r.title || r.path}</span>
                    : <button className="proj-link" onClick={() => (r.kind === 'file' ? downloadResource(r.id) : window.open(r.url, '_blank', 'noopener'))}>{r.title || r.url || r.file_name}</button>}
                  {r.kind === 'file' && <span className="proj-resource-size">{Math.round((r.file_size || 0) / 1024)} KB</span>}
                  <button className="proj-icon-btn" title="Remove" onClick={() => projectsAPI.deleteResource(r.id).then(load)}>×</button>
                </li>
              ))}
              {(project.resources || []).length === 0 && <li className="proj-muted">No resources.</li>}
            </ul>
          </div>

          {/* ── Comments ── */}
          <div className="proj-section">
            <h6>Comments</h6>
            <div className="proj-comment-add">
              <textarea className="proj-input" rows={2} placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} />
              <button className="proj-btn proj-btn-primary" onClick={addComment}>Post</button>
            </div>
            <ul className="proj-comment-list">
              {(project.comments || []).map((c) => (
                <li key={c.id}>
                  <span className="proj-avatar">{initials(c.author_name, c.author_email)}</span>
                  <div>
                    <div className="proj-comment-meta"><strong>{c.author_name || c.author_email}</strong> · {new Date(c.created_at).toLocaleString('en-AU')}{c.edited ? ' (edited)' : ''}</div>
                    <div className="proj-comment-body">{c.body}</div>
                  </div>
                </li>
              ))}
              {(project.comments || []).length === 0 && <li className="proj-muted">No comments yet.</li>}
            </ul>
          </div>

          {/* ── Activity ── */}
          <div className="proj-section">
            <h6>Activity</h6>
            <ul className="proj-activity">
              {(project.activity || []).map((a) => (
                <li key={a.id}>
                  <span className="proj-activity-dot" />
                  <span className="proj-activity-text">
                    <strong>{a.actor_email?.split('@')[0]}</strong> {ACTION_TEXT[a.action] || a.action}
                    {a.detail?.title ? ` "${a.detail.title}"` : ''}
                    {a.detail?.to ? ` → ${STATUS_LABEL[a.detail.to] || a.detail.to}` : ''}
                  </span>
                  <span className="proj-activity-time">{new Date(a.created_at).toLocaleString('en-AU')}</span>
                </li>
              ))}
              {(project.activity || []).length === 0 && <li className="proj-muted">No activity.</li>}
            </ul>
          </div>
        </div>
      </div>

      {openTaskId && (
        <TaskModal
          taskId={openTaskId}
          users={users}
          onClose={() => setOpenTaskId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
